import React from 'react'
import { useTheme } from '@mui/material/styles'
import { observer } from 'mobx-react-lite'

import {
  formatCurrency,
  Loader,
  useGlobalCopyStore,
  useGlobalUiStore,
  useInsightsV2Store,
} from '../../../../common'
import type { DataSeries, DrilldownPayload } from '../../../../core'
import type { BarChartBlockData } from '../../../../core/types/InsightsInstanceSlotData'

import { BarChartV2 } from '../../../components/chartblocks/BarchartV2'
import { EmptyBarChart } from '../../../components/chartblocks/EmptyBarChart'

import type { BlockProps } from '../../types'

import { barChartVariant, buildChartData, buildDataSeries } from './utils'
import { actionBlocksKey } from '../../../mappings/actionBlocksKey'

export const BarchartBlock = observer(
  ({ payload, beatGuid = '' }: BlockProps<BarChartBlockData>) => {
    const theme = useTheme()
    const { openActionDrawer } = useInsightsV2Store()
    const { insights_feed: insightsFeedCopy } = useGlobalCopyStore()
    const { isCopyLoaded } = useGlobalUiStore()
    const { instance_slot, instance_slot_data, widget_type } = payload
    const { data_series, ariaLabel = '', average_amount = 0, legends = [] } = instance_slot_data
    const chartData = buildChartData(buildDataSeries(data_series as DataSeries), ariaLabel)
    const [selectedTabIndex] = React.useState(chartData.length - 1)
    const { color } = barChartVariant(instance_slot, instance_slot_data, theme)

    if (!isCopyLoaded) {
      return <Loader />
    }

    // TODO: Temporary fallback while the backend doesn't always send `legends` on every beat/variant.
    // Remove this once the backend guarantees `legends` on the payload.
    const fallbackLabels = [insightsFeedCopy.general.monthly_amount, insightsFeedCopy.general.trend]

    const [monthlyAmountLabel, trendAmountLabel] = legends.length
      ? legends.map((legend) => legend.label)
      : fallbackLabels

    return chartData.length ? (
      <BarChartV2
        amounts={{
          average: average_amount,
          formattedAverage: formatCurrency(average_amount, '0,0'),
        }}
        color={color}
        data={chartData}
        monthlyAmountLabel={monthlyAmountLabel}
        onBarClick={(index) => {
          if (widget_type !== 'full') return false

          openActionDrawer({
            guid: beatGuid,
            trigger: 'chartBar',
            context: {
              chartBarIndex: index,
              block: actionBlocksKey.TRANSCATION_HISTORY_ACTION_BLOCK,
            } satisfies DrilldownPayload,
          })
        }}
        selectedTabIndex={selectedTabIndex}
        trendAmountLabel={trendAmountLabel}
      />
    ) : (
      <EmptyBarChart />
    )
  },
)
